import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormSelect } from '../../ui/Select'
import { formatFilterValues } from '../utils'

const QuestionFiltersSection = ({
  showSection,
  toggleSection,
  activeFilters,
  questionOptions,
  valueOptions,
  questions,
  fieldErrors,
  onAddFilter,
  onRemoveFilter,
  onQuestionChange,
  onValuesChange
}) => {
  const handleQuestionChange = useCallback((uniqueId, selected) => {
    onQuestionChange(uniqueId, selected?.value)
  }, [onQuestionChange])

  const handleValuesChange = useCallback((uniqueId, selected) => {
    onValuesChange(uniqueId, selected?.map(option => option.value) || [])
  }, [onValuesChange])

  const renderNoOptionsMessage = useCallback(questionId => {
    if (questionId)
      return 'Aucune option disponible'

    return 'Veuillez d\'abord sélectionner une question'
  }, [])

  const handleQuestionChangeForFilter = useCallback(uniqueId =>
    selected => handleQuestionChange(uniqueId, selected), [handleQuestionChange])

  const handleValuesChangeForFilter = useCallback(uniqueId =>
    selected => handleValuesChange(uniqueId, selected), [handleValuesChange])

  const handleRemoveFilter = useCallback(uniqueId =>
    () => onRemoveFilter(uniqueId), [onRemoveFilter])

  const handleNoOptionsMessage = useCallback(questionId =>
    () => renderNoOptionsMessage(questionId), [renderNoOptionsMessage])

  return (
    <div>
      <div
        className="bg-white d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={toggleSection}
      >
        <h6 className="m-0 font-weight-bolder">
          Filtrer par questions
          (
          {activeFilters.length}
          )
        </h6>
        <i className={`fas fa-chevron-${showSection ? 'up' : 'down'}`} />
      </div>
      {showSection ? (
        <div className="pt-3">
          {activeFilters.map(filter => (
            <div key={filter.uniqueId} className="bg-light mb-3">
              <div className="d-flex align-items-center p-3">
                <div className="flex-grow-1">
                  <FormSelect
                    className="mb-2"
                    options={questionOptions}
                    value={filter.questionId ? {
                      value: filter.questionId,
                      label: questions.find(questionItem =>
                        questionItem.id.toString() === filter.questionId.toString()
                      )?.title
                    } : null}
                    placeholder="Sélectionner une question"
                    errorMessage={fieldErrors[`question_${filter.uniqueId}`] || ''}
                    isClearable
                    onChange={handleQuestionChangeForFilter(filter.uniqueId)}
                  />
                  <FormSelect
                    closeMenuOnSelect={false}
                    errorMessage={fieldErrors[`values_${filter.uniqueId}`] || ''}
                    isDisabled={!filter.questionId}
                    noOptionsMessage={handleNoOptionsMessage(filter.questionId)}
                    options={valueOptions(filter.questionId)}
                    placeholder="Sélectionner une réponse"
                    value={(() => {
                      const question = questions.find(item =>
                        item.id.toString() === filter.questionId?.toString()
                      )
                      return formatFilterValues(filter.values, question, valueOptions(filter.questionId))
                    })()}
                    isClearable
                    isMulti
                    showSelectAll
                    onChange={handleValuesChangeForFilter(filter.uniqueId)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-link text-dark p-0 pl-3 hover-danger"
                  onClick={handleRemoveFilter(filter.uniqueId)}
                >
                  <i className="fas fa-trash" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-link text-dark p-0 hover-bold"
            style={{ textDecoration: 'none' }}
            onClick={onAddFilter}
          >
            <i className="fas fa-plus mr-1" />
            {' '}
            Ajouter une question
          </button>
        </div>
      ) : null}
    </div>
  )
}

QuestionFiltersSection.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.shape({
    questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    uniqueId: PropTypes.string
  })).isRequired,
  fieldErrors: PropTypes.objectOf(PropTypes.string).isRequired,
  onAddFilter: PropTypes.func.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  questionOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    }))
  })).isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    context: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      content: PropTypes.string
    }))
  })).isRequired,
  showSection: PropTypes.bool.isRequired,
  toggleSection: PropTypes.func.isRequired,
  valueOptions: PropTypes.func.isRequired
}

export default QuestionFiltersSection